/**
 * LitClubPage — Redesigned community page with card-based layout
 *
 * Key features:
 * - Digest filter bar for specialty-based filtering
 * - Rebranded from "Community" to "LitClub"
 * - Forums per specialty/subspecialty based on user digests
 * - Same card design as LitDigests and LitHub
 * - Users only see/post to forums for their digest specialties
 * - Pro Trial: full access, Non-trial: read-only
 *
 * PHI-Zero: no user text is logged.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { discussionsAPI, preferencesAPI } from '../api';
import { copilotAPI } from '../api';
import { toast } from 'sonner';
import ForumDiscussionCard from '../components/ForumDiscussionCard';
import { SharedDigestFilterBar, type DigestProfileStat } from '../components/shared/SharedDigestFilterBar';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { CrossAppLinks } from '../components/CrossAppLinks';
import {
  getPersistedDigestProfileId,
  setPersistedDigestProfileId,
} from '../utils/sharedDigestSelection';
import {
  Users,
  MessageSquare,
  Search,
  RefreshCw,
  BookOpen,
  Settings,
  Eye,
  Lock,
  Plus,
  Loader2,
  Sparkles,
} from 'lucide-react';
import type { SpecialtyRoom, DiscussionThread } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

// ============================================================================
// LOADING SKELETON
// ============================================================================
const ForumsSkeleton: React.FC = () => (
  <div className="space-y-4" data-testid="forums-skeleton">
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white">
        <div className="flex items-center gap-3 mb-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex gap-4 mb-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// ERROR STATE
// ============================================================================
interface ErrorStateProps {
  onRetry: () => void;
  message?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ onRetry, message }) => (
  <div className="flex items-center justify-center py-16" data-testid="forum-error">
    <Card className="max-w-md">
      <CardContent className="py-8 text-center">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <RefreshCw className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load LitClub</h2>
        <p className="text-gray-600 mb-4">{message || 'Unable to load forums. Please try again.'}</p>
        <Button onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </CardContent>
    </Card>
  </div>
);

// ============================================================================
// EMPTY STATE
// ============================================================================
const EmptyForumsState: React.FC<{ onGoToPreferences: () => void }> = ({ onGoToPreferences }) => (
  <div className="text-center py-16" data-testid="forum-empty">
    <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-900 mb-2">No Forums Available</h3>
    <p className="text-slate-600 mb-6 max-w-md mx-auto">
      Create a digest in a specialty to unlock its LitClub. Join discussions with colleagues in your field.
    </p>
    <Button onClick={onGoToPreferences}>
      <Settings className="h-4 w-4 mr-2" />
      Go to Preferences
    </Button>
  </div>
);

// ============================================================================
// TRIAL EXPIRED BANNER
// ============================================================================
const TrialExpiredBanner: React.FC<{ onUpgrade: () => void }> = ({ onUpgrade }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="trial-expired-banner">
    <div className="flex items-start gap-2 min-w-0">
      <Eye className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-amber-900">Your Pro Trial has ended</p>
        <p className="text-xs text-amber-700">You have read-only access to LitClub. Upgrade to continue participating.</p>
      </div>
    </div>
    <Button size="sm" onClick={onUpgrade} className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white shrink-0">
      Upgrade to Pro
    </Button>
  </div>
);

// ============================================================================
// FORUM ROOM TYPE WITH THREADS
// ============================================================================
interface ForumRoom extends SpecialtyRoom {
  threads?: DiscussionThread[];
  subspecialties?: { id: string; label: string }[];
  can_enter?: boolean;
  can_post?: boolean;
  access_state?: 'active' | 'frozen' | 'none';
  visible_subspecialties?: { id: string; label: string }[];
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const LitClubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { flags, ready: flagsReady } = useFeatureFlags();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDigestRoute = navV2Enabled && location.pathname.includes('/community/digests/');
  const isArticleRoute = navV2Enabled && location.pathname.includes('/community/articles/');

  // State
  const [rooms, setRooms] = useState<ForumRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingThreads, setLoadingThreads] = useState<Record<string, boolean>>({});
  
  // Digest filter state
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    () => getPersistedDigestProfileId()
  );

  // Nav V2: profileId → specialty_id mapping
  const [digestProfiles, setDigestProfiles] = useState<DigestProfileStat[]>([]);
  const [profileSpecialtyMap, setProfileSpecialtyMap] = useState<Record<string, string>>({});
  useEffect(() => {
    (async () => {
      try {
        const stats = await preferencesAPI.getProfilesScreenStats();
        const profiles = stats.profiles || [];
        setDigestProfiles(profiles);
        const map: Record<string, string> = {};
        for (const p of profiles) {
          map[p.profile_id] = p.specialty_id || p.profile_id;
        }
        setProfileSpecialtyMap(map);
      } catch {}
    })();
  }, []);

  const userDigestSpecialtyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const profile of digestProfiles) {
      if (profile.specialty_id) ids.add(profile.specialty_id);
      ids.add(profile.profile_id);
    }
    return ids;
  }, [digestProfiles]);

  // Nav V2: Route-param sync — derive specialty from profileId
  useEffect(() => {
    if (!navV2Enabled) return;
    if (isDigestRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
      setPersistedDigestProfileId(routeParams.profileId);
    } else if (isArticleRoute) {
      // Article route: use profileId from query for context
      const qpProfileId = searchParams.get('profileId');
      if (qpProfileId) {
        setSelectedProfileId(qpProfileId);
        setPersistedDigestProfileId(qpProfileId);
      }
    } else if (!isDigestRoute && !isArticleRoute) {
      const persisted = getPersistedDigestProfileId();
      if (persisted) {
        setSelectedProfileId(persisted);
        navigate(`/community/digests/${persisted}`, { replace: true });
      }
    }
  }, [navV2Enabled, isDigestRoute, isArticleRoute, routeParams.profileId, location.pathname]);

  // Derive the effective specialty_id for filtering rooms
  const effectiveSpecialtyId = useMemo(() => {
    if (!selectedProfileId) return null;
    // If the profileId is already a specialty_id (direct match in rooms), use it
    if (rooms.some(r => r.specialty_id === selectedProfileId)) return selectedProfileId;
    // Otherwise map through profileSpecialtyMap
    return profileSpecialtyMap[selectedProfileId] || selectedProfileId;
  }, [selectedProfileId, profileSpecialtyMap, rooms]);

  // Nav V2: Article-centered mode — find threads referencing pmid
  const [articlePmid, setArticlePmid] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  useEffect(() => {
    if (!navV2Enabled || !isArticleRoute || !routeParams.pmid) {
      setArticlePmid(null);
      return;
    }
    setArticlePmid(routeParams.pmid);
  }, [navV2Enabled, isArticleRoute, routeParams.pmid, location.pathname]);

  // Nav V2: Route-aware chip handler
  // On mobile, jump straight into the specialty forum — room cards are hidden there.
  const handleProfileSelect = useCallback((id: string | null) => {
    setPersistedDigestProfileId(id);
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    if (isMobile && id) {
      const specialtyId =
        profileSpecialtyMap[id] ||
        (rooms.some((r) => r.specialty_id === id) ? id : null) ||
        id;
      navigate(`/litforum/${specialtyId}`);
      return;
    }
    if (navV2Enabled) {
      navigate(id ? `/community/digests/${id}` : '/community');
    } else {
      setSelectedProfileId(id);
    }
  }, [navV2Enabled, navigate, profileSpecialtyMap, rooms]);

  // Determine if user has active trial
  const hasActiveTrial = useMemo(() => {
    if (!user) return false;
    const trialEnds = user.trial_ends_at || user.trial_expires_at;
    if (!trialEnds) return false;
    try {
      const endDate = new Date(trialEnds);
      return endDate > new Date();
    } catch {
      return false;
    }
  }, [user]);

  // General write capability
  const canWriteGeneral = user?.capabilities?.community_write !== false && hasActiveTrial;

  // Load rooms
  const loadRooms = useCallback(async () => {
    setRoomsLoading(true);
    setRoomsError(null);
    try {
      const data = await discussionsAPI.getSpecialtyRooms();
      const roomsWithThreads: ForumRoom[] = data.rooms.map((room: any) => ({
        ...room,
        threads: [],
      }));
      setRooms(roomsWithThreads);

      // Load threads for each accessible room
      for (const room of roomsWithThreads) {
        if (room.can_enter !== false) {
          loadThreadsForRoom(room.specialty_id);
        }
      }
    } catch (err) {
      setRoomsError(err instanceof Error ? err : new Error('Failed to load forums'));
      toast.error('Failed to load LitClub');
    } finally {
      setRoomsLoading(false);
    }
  }, []);

  // Load threads for a specific room
  const loadThreadsForRoom = async (specialtyId: string) => {
    setLoadingThreads((prev) => ({ ...prev, [specialtyId]: true }));
    try {
      const data = await discussionsAPI.getSpecialtyThreads(specialtyId);
      setRooms((prev) =>
        prev.map((room) =>
          room.specialty_id === specialtyId
            ? { ...room, threads: data.threads.slice(0, 5) } // Get latest 5 threads
            : room
        )
      );
    } catch (err) {
      console.error(`Failed to load threads for ${specialtyId}:`, err);
    } finally {
      setLoadingThreads((prev) => ({ ...prev, [specialtyId]: false }));
    }
  };

  // Effect: Load rooms after flags are ready
  useEffect(() => {
    if (flagsReady) {
      loadRooms();
    }
  }, [flagsReady, loadRooms]);

  // Filter rooms by search and selected profile
  const filteredRooms = useMemo(() => {
    let filtered = rooms.filter((room) => room.can_enter !== false);
    
    // Filter by selected digest profile (specialty) — Nav V2 uses mapped specialty_id
    const filterSpecialty = effectiveSpecialtyId;
    if (filterSpecialty) {
      filtered = filtered.filter(
        (room) => room.specialty_id === filterSpecialty
      );
    } else if (userDigestSpecialtyIds.size > 0) {
      filtered = filtered.filter((room) => userDigestSpecialtyIds.has(room.specialty_id));
    }
    
    // Then apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (room) =>
          room.specialty_name.toLowerCase().includes(query) ||
          room.subspecialties?.some((sub) => sub.label.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [rooms, searchQuery, selectedProfileId, navV2Enabled, effectiveSpecialtyId, userDigestSpecialtyIds]);

  // Handlers
  const handleViewForum = (specialtyId: string) => {
    navigate(`/litforum/${specialtyId}`);
  };

  const handleNewThread = (specialtyId: string) => {
    if (!canWriteGeneral && !hasActiveTrial) {
      toast.error('Upgrade to Pro to start discussions');
      return;
    }
    navigate(`/community/new?specialty=${specialtyId}`);
  };

  const handleViewThread = (threadId: string) => {
    navigate(`/community/thread/${threadId}`);
  };

  const digestFilterBar = (
    <SharedDigestFilterBar
      selectedProfileId={selectedProfileId}
      onSelectProfile={handleProfileSelect}
      showUnscreenedBadge={false}
      showSavedPercent={true}
      showPrimaryStar={true}
      showRunDigest={false}
      centered={true}
      allLabel="All Digests"
    />
  );

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!flagsReady || roomsLoading) {
    return (
      <div className="flex flex-col" data-testid="litclub-page">
        {digestFilterBar}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">LitClub</h1>
            <p className="text-slate-600 mt-2">Connect with colleagues in your specialty communities.</p>
          </div>
          <ForumsSkeleton />
        </div>
      </div>
    );
  }

  if (roomsError) {
    return (
      <div className="flex flex-col" data-testid="litclub-page">
        {digestFilterBar}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorState onRetry={loadRooms} message={roomsError.message} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-0 overflow-x-hidden" data-testid="litclub-page">
      {digestFilterBar}
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full min-w-0">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">LitClub</h1>
              <p className="text-slate-600 mt-1 text-sm sm:text-base">
                Connect with colleagues in your specialty communities.
              </p>
            </div>
            {filteredRooms.length > 0 && canWriteGeneral && (
              <Button
                onClick={() => navigate('/community/new')}
                className="bg-sky-600 hover:bg-sky-700 text-white w-full sm:w-auto shrink-0"
                data-testid="global-new-thread-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Discussion
              </Button>
            )}
          </div>

          {/* PHI Warning */}
          <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md inline-block mb-4">
            Reminder: Do not share patient identifiers in discussions.
          </p>

          {/* Trial Expired Banner */}
          {!hasActiveTrial && user && (
            <TrialExpiredBanner onUpgrade={() => navigate('/pricing')} />
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search forums..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="forum-search-input"
            />
          </div>
        </div>

        {/* Nav V2: Article-centered mode — show threads discussing this article */}
        {isArticleRoute && articlePmid && (
          <div className="mb-4" data-testid="litclub-article-centered">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-bold text-slate-800">Discussions about this article</h2>
                <p className="text-xs text-slate-500">PMID {articlePmid}</p>
              </div>
              <button onClick={() => navigate(selectedProfileId ? `/community/digests/${selectedProfileId}` : '/community')}
                className="text-xs text-sky-600 hover:text-sky-700 font-medium">← Back to forums</button>
            </div>
            <CrossAppLinks pmid={articlePmid} profileId={selectedProfileId} originApp="community" className="mb-3" />

            {/* Suggested discussion questions */}
            <div className="mb-4" data-testid="litclub-suggested-questions">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Start a discussion about this article</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'How would this change practice?',
                  'What are the biggest limitations or biases in this study?',
                  'Would you apply this in your setting? Why or why not?',
                ].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/community/new?pmid=${articlePmid}`)}
                    className="px-3 py-1.5 text-xs text-slate-700 bg-white border border-slate-200 rounded-full hover:border-sky-300 hover:text-sky-700 hover:bg-sky-50 transition-colors"
                    data-testid={`suggested-q-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Draft with AI button */}
            <button
              onClick={async () => {
                if (draftLoading || !articlePmid) return;
                setDraftLoading(true);
                try {
                  const result = await copilotAPI.draftPost({
                    specialty_id: effectiveSpecialtyId || 'medicine',
                    pmids: [articlePmid],
                    tone: 'neutral',
                  });
                  // Store in sessionStorage for NewThreadPage to pick up
                  sessionStorage.setItem('litscholar_draft', JSON.stringify({
                    draft_post: result.draft_post,
                    suggested_questions: result.suggested_questions,
                    citations: result.citations,
                    disclaimer: result.disclaimer,
                  }));
                  navigate(`/community/new?pmid=${articlePmid}&from=litscholar`);
                } catch (e) {
                  toast.error('Failed to generate draft. Please try again.');
                  setDraftLoading(false);
                }
              }}
              disabled={draftLoading}
              className="mb-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 hover:border-violet-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="draft-with-ai-btn"
            >
              {draftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {draftLoading ? 'Generating draft...' : 'Draft with AI'}
            </button>

            {(() => {
              // Collect all threads across all rooms that reference this pmid
              const matchingThreads: any[] = [];
              for (const room of rooms) {
                for (const thread of (room.threads || [])) {
                  const attached = thread.attached_articles || thread.attached_article_pmids || [];
                  const pmids = attached.map((a: any) => typeof a === 'string' ? a : a.pmid).filter(Boolean);
                  if (pmids.includes(articlePmid)) {
                    matchingThreads.push({ ...thread, room_specialty: room.specialty_name });
                  }
                }
              }
              if (matchingThreads.length > 0) {
                return (
                  <div className="space-y-2">
                    {matchingThreads.map((t: any) => (
                      <button key={t.thread_id} onClick={() => navigate(`/community/thread/${t.thread_id}`)}
                        className="w-full text-left p-3 bg-white rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-sm transition-all">
                        <p className="responsive-title font-medium text-sm text-slate-800">{t.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {t.room_specialty} · {t.comment_count || 0} comments
                        </p>
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <Card data-testid="litclub-no-discussions">
                  <CardContent className="py-10 text-center">
                    <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-800 mb-1">No discussions yet about this article.</h3>
                    <p className="text-sm text-slate-500 mb-3">Be the first to start a conversation.</p>
                    <button onClick={() => navigate('/community/new')}
                      className="text-sm text-sky-600 hover:text-sky-700 font-medium">Start a Discussion →</button>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        )}

        {/* Forums List — desktop only; mobile selects specialty from the header bar */}
        {!isArticleRoute && (
        <>
          <div className="md:hidden rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center" data-testid="litclub-mobile-specialty-hint">
            <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-medium text-slate-800 mb-1">Choose a specialty</h3>
            <p className="text-sm text-slate-500">
              Select a digest from the bar above to open its LitClub discussions.
            </p>
          </div>
          <ScrollArea className="hidden md:block h-[calc(100vh-280px)]">
            {filteredRooms.length > 0 ? (
              <div className="space-y-4 pr-2" data-testid="forums-list">
                {filteredRooms.map((room, index) => (
                  <ForumDiscussionCard
                    key={room.specialty_id}
                    specialty_id={room.specialty_id}
                    specialty_name={room.specialty_name}
                    thread_count={room.thread_count}
                    member_count={room.member_count}
                    last_activity={room.last_activity}
                    threads={(room.threads || []) as any}
                    index={index}
                    can_enter={room.can_enter}
                    can_post={room.can_post && canWriteGeneral}
                    access_state={room.access_state as any}
                    onViewForum={handleViewForum}
                    onNewThread={handleNewThread}
                    onViewThread={handleViewThread}
                  />
                ))}

                {searchQuery && filteredRooms.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No forums match "{searchQuery}"</p>
                  </div>
                )}
              </div>
            ) : (
              <EmptyForumsState onGoToPreferences={() => navigate('/preferences')} />
            )}
          </ScrollArea>
        </>
        )}
      </div>
    </div>
  );
};

export default LitClubPage;
